/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useRef, useEffect, useState } from 'react';
import isNil from 'lodash/isNil';

// mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

// Styles
import classes from './EditBrand.module.css';

// local components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { EditBrandForm } from './EditBrandForm/EditBrandForm';

// store
import useStore from '@store/index';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { TFormValuesBrand } from '../../types';
import { REGEX_STRING } from '@config/constants/regex';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const EditBrand = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		brandsSelectedRow,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesBrand>({
		initialValues: {
			name: '',
			organization: null,
			countryCode: null,
			place: '',
			legalIdentifier: '',
			contact: null,
			obfuscateIPFS: 'false',
			logo: null,
		},
		validate: {
			name: (value) =>
				REGEX_STRING.test(value)
					? null
					: t('global.nameMincharacter'),
			organization: isNotEmpty(
				t('global.organizationRequired')
			),
			contact: isNotEmpty(t('global.contactRequired')),
			countryCode: isNotEmpty(
				t('global.countryRequired')
			),
			place: (value) =>
				value && REGEX_STRING.test(value)
					? null
					: t('global.placeMincharacter'),
			obfuscateIPFS: isNotEmpty(
				t('global.obfuscateIPFSRequired')
			),
		},
	});

	useEffect(() => {
		if (!isNil(brandsSelectedRow)) {
			form.setValues({
				name: brandsSelectedRow.name,
				organization:
					brandsSelectedRow?.organizationId || '',
				obfuscateIPFS: `${brandsSelectedRow?.obfuscateIPFS}`,
				countryCode: `${brandsSelectedRow?.location?.countryCode || null}`,
				place: `${brandsSelectedRow?.location?.place || null}`,
				contact: `${brandsSelectedRow?.contact?.id || null}`,
				...(brandsSelectedRow?.logo
					? { logo: brandsSelectedRow.logo }
					: {
							logo: null,
						}),
			});

			form.resetDirty();
		}
	}, [brandsSelectedRow]);

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('brands.backToAllBrandsSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('brands.backToAllBrands')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<EditBrandForm
								open={props.opened}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
